import { LoadingButton } from '@mui/lab';
import { Box, Tooltip } from '@mui/material';
import { Filter } from '@searchkit/client';
import papa from 'papaparse';
import React, { useEffect, useRef } from 'react';
import { Reference } from 'sr-types/lib/metadata/v1/graphql';
import { Summary } from 'sr-types/lib/search/v1/graphql';
import { PersonRolesMap, RolePeopleMap } from '../../person/types';
import { CSV_BLOB_TYPE, downloadFile } from '../../supply/report/report';
import { UserProfile } from '../auth/api';
import { ActionType } from '../components/ActionType';
import { enqueueSnackbar } from '../components/Toast';
import { PROFILE, PUNCTUATION_MARKS_CONSTANTS } from '../constants/constants';
import I18n from '../i18n/I18n';
import { AppDefinition } from '../nav/apps';
import { formatDateYYYYMMDD } from './dateTime';
import { stringToColor } from './themeUtils';
import { DEFAULT_TRUNCATION_LENGTH } from './utilsConstants';

export { generateTempId, makeCol } from './gridUtils';
export { getContactNameFromObject } from './nameUtils';

export function stringAvatar(name: string) {
    if (name) {
        const arr = name.split(' ');
        return {
            sx: {
                bgcolor: stringToColor(name)
            },
            children:
                arr.length > 1 ? `${arr[0][0]}${arr[1][0]}` : `${arr[0][0].toUpperCase()}${arr[0][1].toUpperCase()}`
        };
    }
    return {};
}

export const emailObfuscation = (email: string) => {
    const splitEmail = email.split('@');
    const domain = splitEmail[1];
    const name = splitEmail[0];
    const lastTwoLetters = name.slice(-2);
    return name.substring(0, 2).concat('*****').concat(lastTwoLetters).concat('@').concat(domain);
};

export const log = (...args) => {
    if (window.DEBUG) {
        console.log(...args);
    }
};

export function getNestedFieldValue(obj, field) {
    if (obj && typeof obj === 'object') {
        if (obj.hasOwnProperty(field)) {
            return obj[field];
        }
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                const nestedValue = getNestedFieldValue(obj[key], field);
                if (nestedValue !== undefined) {
                    return nestedValue;
                }
            }
        }
    }
    return undefined;
}

export const successToastMessage = (token: string, values = {}) => {
    return enqueueSnackbar(<I18n token={token} values={values} />, { variant: 'success' });
};

export const failedToastMessage = (token: string, values = {}) => {
    return enqueueSnackbar(<I18n token={token} values={values} />, {
        variant: 'error'
    });
};

export const failedToastMessageWithPrefix = (prefix: string, token: string, values: Record<string, any> = {}) => {
    return enqueueSnackbar(
        <Box component="span">
            {`${prefix} `}
            <I18n token={token} values={values} />
        </Box>,
        { variant: 'error' }
    );
};

export const warningToastMessage = (token: string, values = {}) => {
    const message = enqueueSnackbar(<I18n token={token} values={values} />, {
        variant: 'warning'
    });
    return message;
};

export const infoToastMessage = (token: string, values = {}) => {
    return enqueueSnackbar(<I18n token={token} values={values} />, {
        variant: 'info'
    });
};

/**
 * Checks if any of the specified filters are present in the target list of filters.
 *
 * @param targetFilters - The list of filters to search in.
 * @param filtersToCheck - The filters to look for.
 *
 * @returns `true` if any of the filters in `filtersToCheck` are found in `targetFilters`, otherwise `false`.
 */
export const isAnyFilterInList = (targetFilters: Filter[], filtersToCheck: Filter[]) => {
    for (const filterToCheck of filtersToCheck) {
        const foundFilter = targetFilters.find(
            (filter) => filter.identifier === filterToCheck.identifier && filter.value === filterToCheck.value
        );
        if (foundFilter) {
            return true;
        }
    }
    return false;
};

/**
 * Checks if all of the specified filters are present in the target list of filters.
 *
 * @param targetFilters - The list of filters to search in.
 * @param filtersToCheck - The filters to look for.
 *
 * @returns `true` if all filters in `filtersToCheck` are found in `targetFilters`, otherwise `false`.
 */
export const areAllFiltersInList = (targetFilters: Filter[], filtersToCheck: Filter[]) => {
    for (const filterToCheck of filtersToCheck) {
        const foundFilter = targetFilters.find(
            (filter) => filter.identifier === filterToCheck.identifier && filter.value === filterToCheck.value
        );
        if (!foundFilter) {
            return false;
        }
    }
    return true;
};

export const hyperlinkSx = {
    '&:hover': {
        textDecoration: 'underline',
        color: 'primary.main',
        cursor: 'pointer'
    }
};

export const getEntityPermisions = (allowedActions: Array<string>) => {
    const permissions = allowedActions.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
    }, {});
    return permissions;
};

//custom hook outside the component to detect unmount
export const useIsMounted = () => {
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    return () => isMounted.current;
};

export const getIsUserProfileFromPathname = (location) => {
    const pathName = location.pathname.split('/');
    const isUserProfile = pathName[pathName.length - 1] === PROFILE;
    return isUserProfile;
};

export const useIsAppAccessGranted = (
    userProfile: UserProfile,
    activeOrganizationAccount: string,
    APP_NAME: AppDefinition
): boolean => {
    const userProfileMember = userProfile?.memberships?.find(
        (membership) => membership.id === activeOrganizationAccount
    );
    return userProfileMember?.access?.some((access) => access.application === APP_NAME.name) ?? false;
};

export const checkForObject = (obj: object) => {
    return obj && Object.keys(obj).length > 0;
};

export const getActionButtons = (actions: ActionType[], key: string) => {
    return actions.map((action, index) => (
        <Tooltip key={`${key}-action-tooltip-${index}`} title={action?.tooltip || ''}>
            {/* This Box is used to wrap the button for tooltip to work properly when button is disabled */}
            <Box component="span">
                <LoadingButton
                    variant={action.variant}
                    color={action.color}
                    disabled={action.disabled}
                    onClick={(_e) => action.onClick?.()}
                    loading={action?.loading || false}
                    sx={[{ display: action?.hidden ? 'none' : undefined }, action.sx]}
                    data-testid={action.dataTestId}
                >
                    {action.icon ? <action.icon /> : <></>} {action.label}
                </LoadingButton>
            </Box>
        </Tooltip>
    ));
};

export const AIReferenceFieldStyling = (
    val: any,
    aiExtractedValue: boolean,
    aiExtractedFieldColor: Record<string, string>,
    requiresUserCorrection = false
) => {
    const hasValue = checkForObject(val) && aiExtractedValue;

    const borderColor = hasValue
        ? requiresUserCorrection
            ? aiExtractedFieldColor.HIGHLIGHTED
            : aiExtractedFieldColor.NORMAL
        : aiExtractedFieldColor.DEFAULT;

    const borderWidth = hasValue ? '2px' : '1px';

    return {
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor,
            borderWidth
        }
    };
};

export const getAIAdornmentStylingForDatePicker = () => ({
    '& .MuiPickersOutlinedInput-root': {
        paddingLeft: '8px'
    },

    '& .MuiPickersOutlinedInput-root .MuiInputAdornment-positionStart': {
        marginRight: '16px'
    }
});

export const getAIAdornmentStylingForTextArea = () => {
    return {
        '& .MuiOutlinedInput-root': {
            alignItems: 'flex-start', // Aligns children (adornment and input) to the top
            paddingTop: '10px', // Add some top padding for better visual spacing
            paddingLeft: '8px'
        },

        '& .MuiOutlinedInput-root .MuiInputAdornment-positionStart': {
            marginRight: '16px'
        }
    };
};

export const getAIAdornmentStylingForNumber = () => {
    return {
        '& .MuiOutlinedInput-root': {
            paddingLeft: '6px'
        },

        '& .MuiOutlinedInput-root .MuiInputAdornment-positionStart': {
            marginRight: '10px'
        }
    };
};

export const getOptionValue = (label, createOption) => {
    return {
        id: '',
        label,
        ref: {
            id: '',
            label,
            uriPrefix: ''
        },
        createOption
    };
};

export const getPersonOptionValue = (
    initial: string,
    firstName: string,
    lastName: string,
    email: string,
    createOption: boolean
) => {
    if (createOption) {
        return {
            name: `${initial} ${firstName} ${lastName}`,
            firstName,
            lastName,
            contactInfo: {
                email: {
                    address: email
                }
            },
            createOption
        };
    } else {
        return {
            name: {
                firstName: `${initial} ${firstName} ${lastName}`,
                lastName: ''
            },
            firstName,
            lastName,
            contactInfo: {
                email: {
                    address: email
                }
            },
            createOption
        };
    }
};

export const getAiPlaceholderOptions = (
    inputValue: string,
    entity: string,
    addOption: boolean,
    searchForText: string,
    searchAndClearAllText: string
) => {
    const options = [getOptionValue(`${searchForText} ${inputValue}`, false)];
    if (addOption) {
        options.push(getOptionValue(inputValue, true));
    }
    options.push(getOptionValue(`${searchAndClearAllText} ${entity}`, false));
    return options;
};

export const createReferenceFromIdAndName = (id: string, label: string): Reference => {
    return {
        id,
        label
    };
};

export type ToastMessage = {
    success: {
        token: string;
        values?: Record<string, any>;
    };
    error: {
        token: string;
        values?: Record<string, any>;
    };
};

type AggregateResult = {
    byName: string;
    byRole: string;
    byNameArray: PersonRolesMap[];
    byRoleArray: RolePeopleMap[];
};

type ItemAccessors<T> = {
    getId: (item: T, idx: number) => string;
    getName: (item: T) => string;
    getRoles: (item: T) => string[];
};

export function aggregateItemsByNameAndRole<T>(
    items: T[],
    accessors: ItemAccessors<T>,
    noRoleMessage: string
): AggregateResult {
    if (!Array.isArray(items) || items.length === 0) {
        return {
            byName: PUNCTUATION_MARKS_CONSTANTS.dash,
            byRole: PUNCTUATION_MARKS_CONSTANTS.dash,
            byNameArray: [],
            byRoleArray: []
        };
    }

    const normalized = items
        .map((it, idx) => {
            const idRaw = accessors.getId(it, idx);
            const id = idRaw != null ? String(idRaw) : String(idx);
            const rawName = accessors.getName(it) || '';
            const name = String(rawName).replace(/\s+/g, ' ').trim() || PUNCTUATION_MARKS_CONSTANTS.dash;
            const getAccessorsRoles = accessors.getRoles(it);
            const roles = Array.isArray(getAccessorsRoles) ? getAccessorsRoles.filter(Boolean) : [];
            return { id, name, roles };
        })
        .filter(Boolean);

    const byId = {};
    const roleMap: Record<string, Set<{ name: string; id: string }>> = {};
    const byNameArray: PersonRolesMap[] = [];
    const byRoleArray: RolePeopleMap[] = [];

    normalized.forEach((entry) => {
        if (!byId[entry.id]) byId[entry.id] = { name: entry.name, roles: new Set(), id: entry.id };
        /**
         * If an entry has no assigned roles (`entry.roles.length === 0`), a placeholder role
         * represented by `PUNCTUATION_MARKS_CONSTANTS.dash` is added. This placeholder
         * indicates a missing or undefined role.
         */
        if (entry.roles.length === 0) {
            byId[entry.id].roles.add(noRoleMessage);

            if (!roleMap[noRoleMessage]) roleMap[noRoleMessage] = new Set();
            roleMap[noRoleMessage].add({ name: entry.name, id: entry.id });
        }
        entry.roles.forEach((r) => {
            if (!r) return;
            byId[entry.id].roles.add(r);

            if (!roleMap[r]) roleMap[r] = new Set();
            roleMap[r].add({ name: entry.name, id: entry.id });
        });
    });

    // format by name
    const byNameLines = Object.values(byId)
        .map(({ name, roles, id }) => {
            const rolesArr = Array.from(roles as Set<string>).sort((a, b) => a.localeCompare(b));
            const rolesText = rolesArr.length && rolesArr.join(PUNCTUATION_MARKS_CONSTANTS.comma + ' ');

            byNameArray.push({ person: { name, id }, roles: rolesArr });
            return `${name}: ${rolesText}`;
        })
        .sort((a, b) => a.localeCompare(b));

    // format by role
    const roleKeys = Object.keys(roleMap).sort((a, b) => (a || '').localeCompare(b || ''));
    const byRoleLines = roleKeys.map((role) => {
        const people = Array.from(roleMap[role] as Set<{ name: string; id: string }>).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        const namesText = people.length
            ? people.map((n) => n.name).join(` ${PUNCTUATION_MARKS_CONSTANTS.pipe} `)
            : PUNCTUATION_MARKS_CONSTANTS.dash;

        byRoleArray.push({ role, people: people.map((person) => ({ name: person.name, id: person.id })) });
        return `${role}: ${namesText}`;
    });

    return {
        byName: byNameLines.length
            ? byNameLines.join(PUNCTUATION_MARKS_CONSTANTS.newLine)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        byRole: byRoleLines.length
            ? byRoleLines.join(PUNCTUATION_MARKS_CONSTANTS.newLine)
            : PUNCTUATION_MARKS_CONSTANTS.dash,
        byNameArray,
        byRoleArray
    };
}

/**
 * Regex to detect values that spreadsheet applications would interpret as formulas.
 * This helps identify potential CSV injection payloads (e.g., values starting with =, +, -, @).
 * Matches optional leading whitespace followed by a formula-trigger character.
 */
const CSV_INJECTION_REGEX = /^[\s]*[=+\-@]/;

/**
 * Sanitizes a value before placing it into a CSV cell by neutralizing
 * leading spreadsheet formulas.
 *
 * Uses a leading tab character (`\t`) following OWASP guidance to ensure
 * spreadsheet applications treat the value as plain text instead of executing it.
 */
export function sanitizeForCsvCell(value: unknown): string {
    if (value == null) return '';

    const str = String(value);

    // Already sanitized; avoid double-prefixing to keep this idempotent.
    if (str.startsWith('\t')) {
        return str;
    }

    if (CSV_INJECTION_REGEX.test(str)) {
        return `\t${str}`; // Prefix tab to force spreadsheet to treat value as text
    }

    return str;
}

/**
 * Downloads a CSV file generated from the provided table data.
 *
 * @param tableData - The data to be converted into CSV format.
 * @param mapDataToCsvRows - A function that maps the table data to CSV rows (can be async).
 * @param exportFileName - Name to include in the filename.
 * @param ownerId - Owner ID to pass to the mapper function.
 */
export const downloadCsvFile = async <
    TableRow extends object = Record<string, unknown>,
    CsvRow extends Record<string, unknown> = Record<string, unknown>
>(
    tableData: TableRow[] = [],
    mapDataToCsvRows: (data: TableRow[], ownerId: string) => CsvRow[] | Promise<CsvRow[]>,
    exportFileName: string,
    ownerId: string
): Promise<void> => {
    if (!tableData?.length && exportFileName !== 'Associated People') return;

    const rows = await mapDataToCsvRows(tableData, ownerId);

    if (!rows.length) return;

    const exportDate = formatDateYYYYMMDD();
    const sanitizedFileName = exportFileName.replace(/\s+/g, '_');
    const title = `${sanitizedFileName}-As_Of-${exportDate}`;

    const headers = Object.keys(rows[0]);
    const dataRows = rows.map((row: CsvRow) => headers.map((h) => sanitizeForCsvCell(row[h])));

    const csvData = [[], headers, ...dataRows];

    const csv = papa.unparse(csvData, {
        quotes: true,
        skipEmptyLines: true
    });

    const blob = new Blob([csv], { type: CSV_BLOB_TYPE });
    const filename = `${title}.csv`;

    downloadFile(blob, filename);
};

/**
 * Transforms a name value to a display string.
 * Handles both string and object formats { firstName, lastName }.
 */
export function transformNameToDisplay(name: string | { firstName?: string; lastName?: string } | undefined): string {
    if (!name) {
        return '';
    }

    if (typeof name === 'string') {
        return name;
    }

    const firstName = name.firstName || '';
    const lastName = name.lastName || '';
    return `${firstName} ${lastName}`.trim();
}

/**
 * Transforms a location value to a display string.
 * Handles both string and object formats with label property.
 */
export function transformLocationToDisplay(location: string | { label?: string } | undefined): string {
    if (!location) {
        return '-';
    }

    if (typeof location === 'string') {
        return location;
    }

    return location.label || '-';
}

/**
 * Transforms a summary value to display strings for about and headline.
 */
export function transformSummaryToDisplay(summary: Summary | undefined): {
    displayAbout: string;
    displayHeadline: string;
} {
    if (!summary) {
        return { displayAbout: '', displayHeadline: '' };
    }

    return { displayAbout: summary.about || '', displayHeadline: summary.headline || '' };
}

export const extractItemId = (item: unknown): string | undefined => {
    if (typeof item !== 'object' || item === null) return undefined;

    const obj = item as {
        identity?: { id?: string };
        id?: string;
    };

    return obj.identity?.id ?? obj.id;
};

export function entityToOptionReference(entity) {
    return {
        ...entity.reference
    };
}

/**
 * Truncates a string to a specified maximum length, adding ellipsis if necessary.
 *
 * @param text - The text to truncate.
 * @param maxLength - The maximum length of the truncated text.
 * @returns The truncated text with ellipsis if it exceeds the maximum length.
 */
export const truncateText = (text: string, maxLength = DEFAULT_TRUNCATION_LENGTH): string => {
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.slice(0, maxLength - 1)}…`;
};
